import React, { useCallback, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM, EyebrowMD, HeadingXS, TitleLG, TitleSM, TitleXS } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { Modal } from '@/molecules/Modal'
import { useTranslate } from '@/utils/translate/translate-client'
import { FlexibleBookingToolTip } from '../ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '../ToolTips/SpecificTimesToolTip'

interface NoFeesModalProps {
  open: boolean
  onClose: () => void
  isManualCode?: boolean
  theatricalSlug?: string
  code: string
}
const getModalBg = (theatricalSlug?: string) => {
  switch (theatricalSlug) {
    case 'bonhoeffer':
      return '!bg-[url(https://images.angelstudios.com/image/upload/v1727392065/bonhoeffer/bonhoeffer_early_bird_modal.jpg)]'
    case 'homestead':
      return '!bg-[url(https://images.angelstudios.com/image/upload/v1734918551/Homestead/homestead-poster-tall-02.png)]'
    default:
      return 'bg-black'
  }
}
export const NoFeesModal = ({ theatricalSlug, open, onClose, code }: NoFeesModalProps) => {
  const { t } = useTranslate('tickets')
  const [copiedCode, setCopiedCode] = useState(false)
  const handleCopyCode = useCallback(() => {
    if (code != null) {
      navigator.clipboard.writeText(code)
      setCopiedCode(true)
    }
  }, [code])

  if (theatricalSlug === slugs.kingOfKings) {
    return (
      <Modal
        backgroundScreenColor="black"
        isOpen={open}
        onClose={onClose}
        backgroundOverlayClassName="!bg-black/85"
        panelClasses={classNames(
          `!bg-[#CDE4E9] bg-bottom bg-cover !text-black !max-w-[380px] shadow-dark-4 rounded-3xl !p-6 !pt-10 relative text-center !overflow-visible`,
        )}
        transition="appear"
        className="z-[99999]"
      >
        <div>
          <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-black/20 p-1" onClick={onClose}>
            <CloseIcon color="black" size={18} />
          </div>
          <div className={'relative -mt-2 h-[358px]'}>
            <div className={'relative inset-0 h-[334px] w-full text-black'}>
              <div className={'w-[calc(100% + 1em)] -mx-6 flex h-[334px] flex-row'}>
                <div className={'relative basis-1/2'}>
                  <div className={'absolute left-[0.5em] top-[5.4em]'}>
                    <Image src={'/v1743733530/king-of-kings/willia_b_1.webp'} width={86} height={99} alt={''} />
                  </div>
                </div>
                <div className={'relative basis-1/2'}>
                  <div className={'absolute bottom-[0.8em] right-[0.5em]'}>
                    <Image src={'/v1743733534/king-of-kings/walter_c_1.webp'} width={77} height={189} alt={''} />
                  </div>
                </div>
              </div>
            </div>
            <div className={'absolute inset-0 z-20'}>
              <FlexColumn className="relative z-20 gap-6">
                <FlexColumn className="gap-1">
                  <HeadingXS weight="bold" className="my-2 w-[70%] self-center">
                    {t('noOnlineFeesNoHassleV1', 'No Online Fees. No Hassle.')}
                  </HeadingXS>
                  <FlexRow className="mb-1 justify-center gap-1">
                    <SpecificTimesToolTip buttonColor={'black'}>
                      <TitleXS>{t('onSelectShowtimes', 'On Select Showtimes')}</TitleXS>
                    </SpecificTimesToolTip>
                  </FlexRow>
                  <FlexibleBookingToolTip
                    className="mx-auto w-fit"
                    panelPosition="CENTER"
                    buttonColor={'black'}
                    buttonClassName={classNames(
                      'backdrop-blur border-none py-1 w-full bg-black/20 backdrop-filter backdrop-blur-[24px]',
                    )}
                  />
                </FlexColumn>
                <FlexColumn className="w-fit self-center">
                  <EyebrowMD className="text-[0.938rem] uppercase">{t('usePromoCode', 'Use Promo Code')}</EyebrowMD>
                  <TitleLG className="my-1 w-fit self-center bg-[#EA3358] px-3 py-2 text-white" weight="bold">
                    {code}
                  </TitleLG>
                  <EyebrowMD className="text-[0.938rem] uppercase">{t('atCheckout', 'At Checkout')}</EyebrowMD>
                </FlexColumn>
                <FlexColumn className="mt-2 items-center gap-3">
                  <Button onClick={handleCopyCode} className="flex w-fit items-center gap-1" variant="black">
                    {copiedCode ? <CheckIcon size={24} color="white" /> : <CopyIcon size={24} color="white" />}
                    {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
                  </Button>
                  <CaptionSM italic color="core-gray-700">
                    {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
                  </CaptionSM>
                </FlexColumn>
              </FlexColumn>
            </div>
          </div>
        </div>
      </Modal>
    )
  }
  const bg = getModalBg(theatricalSlug)
  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      backgroundOverlayClassName="!bg-black/85"
      panelClasses={classNames(
        bg,
        `bg-bottom bg-cover !text-white !max-w-[380px] shadow-dark-4 rounded-3xl !p-6 !pt-10 relative text-center !overflow-visible`,
      )}
      transition="appear"
      className="z-[99999]"
    >
      <div>
        <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-white/20 p-1" onClick={onClose}>
          <CloseIcon color="white" size={18} />
        </div>
        <FlexColumn className="relative z-10 gap-6">
          <FlexColumn className="gap-1">
            <HeadingXS weight="bold" className="my-2 w-[70%] self-center">
              {t('noOnlineFeesNoHassleV1', 'No Online Fees. No Hassle.')}
            </HeadingXS>
            <FlexRow className="justify-center gap-1">
              <SpecificTimesToolTip>
                <TitleSM>{t('onSelectShowtimes', 'On Select Showtimes')}</TitleSM>
              </SpecificTimesToolTip>
            </FlexRow>
            <FlexibleBookingToolTip
              className="mx-auto w-fit"
              panelPosition="CENTER"
              buttonClassName={classNames(
                'backdrop-blur border-none w-full bg-white/20 backdrop-filter backdrop-blur-[24px]',
              )}
            />
          </FlexColumn>
          <FlexColumn className="w-fit self-center">
            <EyebrowMD className="text-[0.938rem] uppercase">{t('useCode', 'Use Code')}</EyebrowMD>
            <TitleLG className="my-1 w-fit self-center bg-[#F2A31E] px-3 py-2" weight="bold">
              {code}
            </TitleLG>
            <EyebrowMD className="text-[0.938rem] uppercase">{t('atCheckout', 'At Checkout')}</EyebrowMD>
          </FlexColumn>
          <FlexColumn className="items-center gap-3">
            <Button onClick={handleCopyCode} className="flex w-fit items-center gap-1" variant="white">
              {copiedCode ? (
                <CheckIcon size={24} color="core-gray-950" />
              ) : (
                <CopyIcon size={24} color="core-gray-950" />
              )}
              {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
            </Button>
            <CaptionSM italic color="core-gray-400">
              {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
            </CaptionSM>
          </FlexColumn>
        </FlexColumn>
      </div>
    </Modal>
  )
}
